import React, {useCallback, useMemo} from 'react';
import './DivisionRoleConfigurator.css';
import CustomCheckbox from '../CustomCheckbox/CustomCheckbox';
import CreateRoleButtonActions from '../CreateRoleButtonActions/CreateRoleButtonActions';
import {ReactComponent as CheckboxIcon} from '../../images/checkboxIcon.svg';

const DivisionRoleConfigurator = React.memo(
  ({selectedTab, handleChangeFormData, data, toggleColumn}) => {
    // 🧩 Защита от отсутствующих данных
    const rightsEntries = useMemo(
      () => Object.entries(data?.rights ?? {}),
      [data]
    );

    // 🧠 Мемоизация списка типов
    const typeKeys = useMemo(() => {
      if (rightsEntries.length === 0) return [];
      const firstRight = rightsEntries[0][1];
      if (!firstRight?.types) return [];
      return Object.entries(firstRight.types).map(([typeId, type]) => ({
        id: typeId,
        name: type?.name ?? '—',
      }));
    }, [rightsEntries]);

    // ✅ Обновление конкретного чекбокса
    const handleCheckboxChange = useCallback(
      (rightId, typeId, isChecked) => {
        handleChangeFormData({
          sectionId: selectedTab,
          rightId,
          typeId,
          value: isChecked ? 1 : 0,
        });
      },
      [handleChangeFormData, selectedTab]
    );

    // ✅ Отметить все / Снять все
    const selectAll = useCallback(() => {
      handleChangeFormData({sectionId: selectedTab, action: 'selectAll'});
    }, [handleChangeFormData, selectedTab]);

    const deselectAll = useCallback(() => {
      handleChangeFormData({sectionId: selectedTab, action: 'deselectAll'});
    }, [handleChangeFormData, selectedTab]);

    if (!data || !data.rights || rightsEntries.length === 0) {
      return (
        <div className='divisionRole-configurator__empty'>
          Нет данных для отображения
        </div>
      );
    }

    if (typeKeys.length === 0) {
      return (
        <div className='divisionRole-configurator__empty'>
          Нет доступных типов прав
        </div>
      );
    }

    return (
      <div className='divisionRole-configurator'>
        <CreateRoleButtonActions
          selectAll={selectAll}
          deselectAll={deselectAll}
        />

        <div
          className='divisionRole-configurator__wrapper'
          style={{gridTemplateColumns: `0fr repeat(${typeKeys.length}, 1fr)`}}
        >
          {/* Заголовок */}
          <div className='divisionRole-configurator__head'>
            <div className='divisionRole-configurator__head-cell'></div>
            {typeKeys.map((type) => (
              <div
                key={type.id}
                className='divisionRole-configurator__head-cell'
                onClick={() => {
                  // Проверяем, все ли чекбоксы этого столбца уже отмечены
                  const allChecked = Object.values(data.rights).every(
                    (right) => right.types[type.id]?.value === 1
                  );

                  // Переключаем только этот столбец
                  Object.entries(data.rights).forEach(([rightId]) => {
                    handleChangeFormData({
                      sectionId: selectedTab, // текущий таб
                      rightId,
                      typeId: type.id,
                      value: allChecked ? 0 : 1, // снимаем или ставим
                    });
                  });
                }}
              >
                <span className='divisionRole-configurator__title'>
                  {type.name}
                </span>
                <CheckboxIcon />
              </div>
            ))}
          </div>

          {/* Тело таблицы */}
          <div className='divisionRole-configurator__body'>
            {rightsEntries.map(([rightId, right]) => (
              <div key={rightId} className='divisionRole-configurator__row'>
                <div className='divisionRole-configurator__cell'>
                  <span className='divisionRole-configurator__title'>
                    {right?.name ?? 'Без названия'}
                  </span>
                </div>

                {typeKeys.map((type) => {
                  const typeData = right?.types?.[type.id];
                  return (
                    <div
                      key={type.id}
                      className='divisionRole-configurator__cell'
                    >
                      <CustomCheckbox
                        checked={!!typeData?.value}
                        onChange={(isChecked) =>
                          handleCheckboxChange(rightId, type.id, isChecked)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

export default DivisionRoleConfigurator;
